<script setup lang="ts">
import { RouterLink } from 'vue-router'

import BaseIcon from '@/components/base/BaseIcon.vue'
import { useActiveNavItem } from '@/composables/navigation/use-active-nav-item.js'
import type { NavItem } from '@/constants/app/navigation.constant.js'

/**
 * One section's screens, as links.
 *
 * Shared by the section nav and by the rail's flyout, so the two cannot drift: the same
 * icons, the same active rule, and the same treatment for a screen that is not built yet.
 *
 * Two states that must look different, because conflating them produces support tickets:
 *
 * - **absent** — the operator lacks the permission. The item is filtered out upstream;
 *   there is no greyed hint that access exists.
 * - **not built** — rendered, non-navigating, with a `lock` glyph and a reason on hover
 *   *and* in the accessible name. Never a native `disabled`: that leaves the tab order and
 *   takes the explanation with it.
 */
defineProps<{ items: readonly NavItem[] }>()
const emit = defineEmits<{ chose: [] }>()

const active = useActiveNavItem()
</script>

<template>
  <ul class="flex flex-col gap-0.5">
    <li
      v-for="item in items"
      :key="item.label"
    >
      <RouterLink
        v-if="item.available && item.route !== null"
        :to="{ name: item.route, query: item.query ?? {} }"
        :class="[
          'flex items-center gap-2.5 rounded-control px-2 py-1.5 text-body transition-colors duration-(--motion-fast)',
          active(item) ? 'bg-nav-active font-medium text-action' : 'text-nav-ink hover:bg-sunken',
        ]"
        :aria-current="active(item) ? 'page' : undefined"
        @click="emit('chose')"
      >
        <BaseIcon
          :name="item.icon"
          :size="16"
          class="shrink-0"
        />
        <span class="truncate-cell flex-1">{{ item.label }}</span>
        <BaseIcon
          name="caret-right"
          :size="14"
          class="shrink-0 opacity-60"
        />
      </RouterLink>

      <span
        v-else
        class="flex cursor-not-allowed items-center gap-2.5 rounded-control px-2 py-1.5 text-body text-ink-disabled"
        :title="`${item.label} arrives in a later slice`"
        :aria-disabled="true"
      >
        <BaseIcon
          :name="item.icon"
          :size="16"
          class="shrink-0"
        />
        <span class="flex-1">{{ item.label }}</span>
        <!-- The reason, reachable by screen reader as well as by pointer. -->
        <span class="sr-only">— not built yet</span>
        <BaseIcon
          name="lock-simple"
          :size="14"
          class="shrink-0"
        />
      </span>
    </li>
  </ul>
</template>
